/**
 * ForensicRoastTeaser — Locked skeleton preview for forensic analysis.
 *
 * Shows shimmer lines + pulsing dots to incentivize save.
 * Zero API calls. Analysis generates AFTER save, not during verdict reveal.
 */

import { T } from '@/styles/tokens';

interface ForensicRoastTeaserProps {
  isPremium: boolean;
  onUnlockTap?: () => void;
}

export function ForensicRoastTeaser({ isPremium, onUnlockTap }: ForensicRoastTeaserProps) {
  return (
    <div style={{
      backgroundColor: T.white,
      borderRadius: 20,
      border: `1px solid ${T.border}`,
      boxShadow: T.shadowRaised,
      padding: 20,
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: T.primarySubtle,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke={T.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
          </svg>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 900, textTransform: 'uppercase' as const,
          letterSpacing: '0.15em', color: T.textMuted, fontFamily: T.fontFamily,
        }}>
          Forensic Analysis
        </span>
      </div>

      {/* Shimmer skeleton lines */}
      <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
        {[92, 78, 85, 60].map((w, i) => (
          <div key={i} style={{
            height: 12, width: `${w}%`, borderRadius: 6,
            backgroundImage: `linear-gradient(90deg, ${T.cream}, ${T.borderLight}, ${T.cream})`,
            backgroundSize: '200% 100%',
            animation: `shimmer 2s ease-in-out ${i * 0.15}s infinite`,
          }} />
        ))}
      </div>

      {/* CTA / unlock area */}
      <div style={{
        marginTop: 16, paddingTop: 14,
        borderTop: `1px solid ${T.borderLight}`,
      }}>
        {isPremium ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', gap: 3 }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: 5, height: 5, borderRadius: '50%',
                  backgroundColor: T.primary,
                  animation: `bsPulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                }} />
              ))}
            </div>
            <span style={{
              fontSize: 13, fontWeight: 600, color: T.primary, fontFamily: T.fontFamily,
            }}>
              Generates on save.
            </span>
          </div>
        ) : (
          <>
            <p style={{
              margin: 0, fontSize: 13, color: T.textSecondary, fontFamily: T.fontFamily,
              lineHeight: 1.45,
            }}>
              Your first Price Check included a full ✦ Forensic Roast.
            </p>
            <button
              onClick={onUnlockTap}
              style={{
                marginTop: 6, padding: 0, background: 'none', border: 'none',
                fontSize: 13, fontWeight: 600, color: T.primary,
                fontFamily: T.fontFamily, cursor: 'pointer',
              }}
            >
              Unlock for every Price Check →
            </button>
          </>
        )}
      </div>
    </div>
  );
}
